import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveAction,
  executeAction,
  listActions,
  rejectAction,
  requestAction,
  type ActionStatus,
  type ApprovalPayload,
  type ExecutionPayload,
  type RejectionPayload,
} from '../services/action';

export const actionsKeys = {
  root: ['actions'] as const,
  list: (status: ActionStatus | 'all' = 'all') => [...actionsKeys.root, 'list', status] as const,
};

export const useActions = (status: ActionStatus | 'all' = 'all') => {
  return useQuery(actionsKeys.list(status), () => listActions(status));
};

export const useRequestAction = () => {
  const client = useQueryClient();
  return useMutation(requestAction, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: actionsKeys.list('all') });
      client.invalidateQueries({ queryKey: actionsKeys.list('pending') });
    },
  });
};

export const useApproveAction = () => {
  const client = useQueryClient();
  return useMutation(
    ({ actionId, payload }: { actionId: string; payload?: ApprovalPayload }) =>
      approveAction(actionId, payload),
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: actionsKeys.list('all') });
        client.invalidateQueries({ queryKey: actionsKeys.list('pending') });
      },
    },
  );
};

export const useRejectAction = () => {
  const client = useQueryClient();
  return useMutation(
    ({ actionId, payload }: { actionId: string; payload: RejectionPayload }) =>
      rejectAction(actionId, payload),
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: actionsKeys.list('all') });
        client.invalidateQueries({ queryKey: actionsKeys.list('pending') });
      },
    },
  );
};

export const useExecuteAction = () => {
  const client = useQueryClient();
  return useMutation(
    ({ actionId, payload }: { actionId: string; payload?: ExecutionPayload }) =>
      executeAction(actionId, payload),
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: actionsKeys.list('all') });
        client.invalidateQueries({ queryKey: actionsKeys.list('pending') });
      },
    },
  );
};
